/*
  Normalizes CSS color strings the design manager can encounter into a form
  TinyColor understands. Tailwind v4 ships its palettes as `oklch(…)` strings,
  which TinyColor cannot parse — picking a gray tone stored oklch values and
  every derived --tailwind-* triple collapsed to black (#125).

  The oklch → sRGB conversion below is duplicated in ruby.
  Dont forget to update it as well: app/services/theme_service/theme_to_css.rb
 */

const OKLCH_PATTERN =
  /^oklch\(\s*([\d.]+%?|none)\s+([\d.]+%?|none)\s+([\d.]+(?:deg)?|none)/i;

export default function normalizeCssColor(color: string): string {
  if (typeof color !== "string") return color;

  const match = color.match(OKLCH_PATTERN);
  if (!match) return color;

  const l = component(match[1], 1);
  // Per CSS spec, a chroma percentage maps to the 0–0.4 range.
  const c = component(match[2], 0.4);
  const h = match[3].toLowerCase() === "none" ? 0 : parseFloat(match[3]);

  return oklchToHex(l, c, h);
}

function component(value: string, percentScale: number): number {
  if (value.toLowerCase() === "none") return 0;
  if (value.endsWith("%")) return (parseFloat(value) / 100) * percentScale;
  return parseFloat(value);
}

// Björn Ottosson's reference implementation: oklch → OKLab → LMS → linear
// sRGB → gamma-encoded sRGB.
function oklchToHex(l: number, c: number, hDeg: number): string {
  const hRad = (hDeg * Math.PI) / 180;
  const a = c * Math.cos(hRad);
  const b = c * Math.sin(hRad);

  const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = l - 0.0894841775 * a - 1.291485548 * b;

  const l3 = l_ ** 3;
  const m3 = m_ ** 3;
  const s3 = s_ ** 3;

  const rgb = [
    4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3,
    -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3,
    -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3,
  ];

  return (
    "#" +
    rgb
      .map((channel) => {
        const srgb =
          channel <= 0.0031308
            ? 12.92 * channel
            : 1.055 * Math.pow(Math.max(channel, 0), 1 / 2.4) - 0.055;
        return Math.round(Math.min(Math.max(srgb, 0), 1) * 255)
          .toString(16)
          .padStart(2, "0");
      })
      .join("")
  );
}
